/**
 * Settings → Devices admin page
 *
 * Lists every device that has installed Prism (mobile, PWA, browser) for any
 * member, and lets an Admin approve / block / re-enroll them. Until a device
 * is Approved (or the OC_Devices list isn't provisioned), AccessGate keeps
 * non-admins on the "Approval required" screen.
 */
import { useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Title2,
  Body2,
  Caption1,
  Badge,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  ArrowClockwise24Regular,
} from '@fluentui/react-icons';
import { DataTable, type Column } from '@/components/DataTable';
import { useSharePointList } from '@/hooks/useSharePointList';
import { useAppContext } from '@/store/appContext';
import { setDeviceStatus } from '@/graph/deviceEnrollment';
import { canManageRoles } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import { formatDate } from '@/utils/formatters';
import type {
  DeviceEnrollment,
  DeviceStatus,
  SPListItem,
} from '@/models/types';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
  },
});

const STATUS_BADGE: Record<
  DeviceStatus,
  { color: 'success' | 'warning' | 'danger' | 'informative'; label: string }
> = {
  Approved: { color: 'success', label: 'Approved' },
  Pending: { color: 'warning', label: 'Pending' },
  Blocked: { color: 'danger', label: 'Blocked' },
};

export function Devices() {
  const styles = useStyles();
  const { graphClient, currentUser, listIds } = useAppContext();
  const canManage = currentUser ? canManageRoles(currentUser.role) : false;

  const {
    items: devices,
    loading,
    refresh,
  } = useSharePointList<DeviceEnrollment>('OC_Devices', {
    autoFetch: !!listIds?.OC_Devices,
  });

  const [filter, setFilter] = useState<'all' | DeviceStatus>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (currentUser && !canManage) {
    return (
      <AccessDenied reason="Device management is restricted to administrators." />
    );
  }

  if (listIds && !listIds.OC_Devices) {
    return <DevicesSetupPanel />;
  }

  const handleSetStatus = async (
    item: SPListItem<DeviceEnrollment>,
    status: DeviceStatus,
  ) => {
    if (!graphClient || !currentUser) return;
    setBusyId(item.id);
    setError(null);
    try {
      await setDeviceStatus(graphClient, item.id, status, currentUser.email);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status update failed');
    } finally {
      setBusyId(null);
    }
  };

  const counts = useMemo(() => {
    const c = { all: devices.length, Pending: 0, Approved: 0, Blocked: 0 };
    for (const d of devices) {
      const s = (d.fields.Status ?? 'Pending') as DeviceStatus;
      c[s] = (c[s] ?? 0) + 1;
    }
    return c;
  }, [devices]);

  const filtered = useMemo(
    () =>
      filter === 'all'
        ? devices
        : devices.filter((d) => (d.fields.Status ?? 'Pending') === filter),
    [devices, filter],
  );

  const columns: Column<SPListItem<DeviceEnrollment>>[] = [
    {
      key: 'device',
      label: 'Device',
      sortable: true,
      getValue: (i) => i.fields.Title,
      render: (i) => (
        <div>
          <Body2 style={{ fontWeight: 500 }}>
            {i.fields.Model || 'Unknown device'}
          </Body2>
          <Caption1 className={styles.meta}>
            {i.fields.Platform} · {i.fields.OSVersion} · v
            {i.fields.AppVersion}
          </Caption1>
        </div>
      ),
    },
    {
      key: 'user',
      label: 'User',
      sortable: true,
      getValue: (i) => i.fields.Email,
      render: (i) => (
        <div>
          <Body2>{i.fields.Email}</Body2>
          <Caption1 className={styles.meta}>{i.fields.InstallSource}</Caption1>
        </div>
      ),
    },
    {
      key: 'firstSeen',
      label: 'Installed',
      sortable: true,
      getValue: (i) => i.fields.FirstSeen,
      render: (i) => <Caption1>{formatDate(i.fields.FirstSeen)}</Caption1>,
    },
    {
      key: 'lastSeen',
      label: 'Last seen',
      sortable: true,
      getValue: (i) => i.fields.LastSeen,
      render: (i) => <Caption1>{formatDate(i.fields.LastSeen)}</Caption1>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      getValue: (i) => i.fields.Status,
      render: (i) => {
        const s = (i.fields.Status ?? 'Pending') as DeviceStatus;
        const cfg = STATUS_BADGE[s];
        return (
          <Badge appearance="filled" color={cfg.color} size="small">
            {cfg.label}
          </Badge>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '210px',
      render: (i) => {
        const s = (i.fields.Status ?? 'Pending') as DeviceStatus;
        const isBusy = busyId === i.id;
        return (
          <div style={{ display: 'flex', gap: 6 }}>
            {s !== 'Approved' && (
              <Button
                appearance="primary"
                size="small"
                icon={
                  isBusy ? <Spinner size="tiny" /> : <CheckmarkCircle24Regular />
                }
                disabled={isBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleSetStatus(i, 'Approved');
                }}
              >
                Approve
              </Button>
            )}
            {s !== 'Blocked' && (
              <Button
                appearance="outline"
                size="small"
                icon={<DismissCircle24Regular />}
                disabled={isBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleSetStatus(i, 'Blocked');
                }}
              >
                Block
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Devices</PageTitle>
        <Caption1>
          {counts.all} enrolled · {counts.Pending} pending ·{' '}
          {counts.Approved} approved · {counts.Blocked} blocked
        </Caption1>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <DataTable
        columns={columns}
        items={filtered}
        loading={loading}
        searchPlaceholder="Search by user, device, platform..."
        searchFields={(i) =>
          `${i.fields.Email} ${i.fields.Model} ${i.fields.Platform} ${i.fields.OSVersion} ${i.fields.InstallSource}`
        }
        getRowKey={(i) => i.id}
        emptyMessage="No devices enrolled yet."
        renderMobile={(i) => {
          const s = (i.fields.Status ?? 'Pending') as DeviceStatus;
          const cfg = STATUS_BADGE[s];
          return {
            title: i.fields.Model || 'Unknown device',
            subtitle: i.fields.Email,
            meta: `${i.fields.Platform} · ${i.fields.InstallSource} · v${i.fields.AppVersion}`,
            trailing: (
              <Badge appearance="filled" color={cfg.color} size="small">
                {cfg.label}
              </Badge>
            ),
          };
        }}
        filterTabs={[
          { key: 'all', label: 'All', count: counts.all },
          { key: 'Pending', label: 'Pending', count: counts.Pending },
          { key: 'Approved', label: 'Approved', count: counts.Approved },
          { key: 'Blocked', label: 'Blocked', count: counts.Blocked },
        ]}
        activeFilter={filter}
        onFilterChange={(k) => setFilter(k as typeof filter)}
        actions={
          <Button
            appearance="outline"
            icon={<ArrowClockwise24Regular />}
            onClick={() => void refresh()}
          >
            Refresh
          </Button>
        }
      />
    </div>
  );
}

/**
 * Setup panel shown when OC_Devices list isn't provisioned yet.
 * Admin clicks → app creates the list. Mirrors the Cost Allocation setup.
 */
function DevicesSetupPanel() {
  const { graphClient, refreshListIds } = useAppContext();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const setup = async () => {
    if (!graphClient) return;
    setBusy(true);
    setErr(null);
    try {
      const { ensureListExists } = await import('@/graph/sharePointService');
      await ensureListExists(graphClient, 'OC_Devices', [
        { name: 'Email', type: { text: {} } },
        { name: 'DeviceId', type: { text: {} } },
        { name: 'Platform', type: { text: {} } },
        { name: 'Model', type: { text: {} } },
        { name: 'OSVersion', type: { text: {} } },
        { name: 'AppVersion', type: { text: {} } },
        { name: 'InstallSource', type: { text: {} } },
        { name: 'FirstSeen', type: { dateTime: {} } },
        { name: 'LastSeen', type: { dateTime: {} } },
        { name: 'IPAddress', type: { text: {} } },
        { name: 'UserAgent', type: { text: { allowMultipleLines: true } } },
        {
          name: 'Status',
          type: { choice: { choices: ['Pending', 'Approved', 'Blocked'] } },
        },
        { name: 'ApprovedBy', type: { text: {} } },
        { name: 'ApprovedAt', type: { dateTime: {} } },
        { name: 'Notes', type: { text: { allowMultipleLines: true } } },
      ]);
      setDone(true);
      // Soft refresh — refetch list ids context. No full page reload, so we
      // don't 404 if the SWA cached a stale index.html.
      await refreshListIds();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
      <Title2>Devices</Title2>
      <Body2>
        Device enrollment needs a SharePoint list (<code>OC_Devices</code>)
        to track every install of Prism (mobile, PWA, browser). Click below
        to create it now.
      </Body2>
      {err && (
        <MessageBar intent="error">
          <MessageBarBody>{err}</MessageBarBody>
        </MessageBar>
      )}
      {done && (
        <MessageBar intent="success">
          <MessageBarBody>List created — reloading…</MessageBarBody>
        </MessageBar>
      )}
      <Button
        appearance="primary"
        size="large"
        onClick={setup}
        disabled={busy || done}
        style={{ alignSelf: 'flex-start' }}
      >
        {busy ? 'Creating list…' : done ? 'Done' : 'Set up Devices'}
      </Button>
    </div>
  );
}
